import { useCallback, useEffect, useState } from "react";

import { skipToken, useGetUserQuery } from "metabase/api";

import { getFromIDB, putToIDB } from "../Common/indexed-db";

const API_TOKEN =
  "lbr34DsFLEbJ3rbIkIKf4IRtODlluH17QP3EM5042TJQMbovtiEiDWmUFooKPEDt";

export interface ExtendMetadata {
  maintainer?: string[];
  categories?: string[];
}

// Simple in-memory cache to share state between Header and Sidebar
const metadataCache: Record<string, ExtendMetadata> = {};
const listeners: Record<string, Set<(data: ExtendMetadata) => void>> = {};
const fetchedKeys = new Set<string>();

const getCacheKey = (type: string, id: number) => `${type}:${id}`;

const notifyListeners = (key: string, data: ExtendMetadata) => {
  if (listeners[key]) {
    listeners[key].forEach((listener) => listener(data));
  }
};

export const useReportMetadata = (
  reportId: number,
  reportType: "question" | "dashboard",
  reportName: string,
  creatorName?: string,
  creatorId?: number,
) => {
  const key = getCacheKey(reportType, reportId);
  const [metadata, setMetadata] = useState<ExtendMetadata>(
    metadataCache[key] || {},
  );
  const [isLoading, setIsLoading] = useState(false);

  const { data: creator } = useGetUserQuery(creatorId ?? skipToken);
  const effectiveCreatorName =
    creatorName ||
    (creator
      ? creator.common_name || creator.first_name + " " + creator.last_name
      : "Creator");

  // Subscribe to cache updates
  useEffect(() => {
    if (!listeners[key]) {
      listeners[key] = new Set();
    }
    listeners[key].add(setMetadata);

    return () => {
      listeners[key]?.delete(setMetadata);
      if (listeners[key]?.size === 0) {
        delete listeners[key];
      }
    };
  }, [key]);

  const fetchMetadata = useCallback(async () => {
    // 1. Try to load from IDB in parallel (do not await, so we don't block the network request)
    if (!metadataCache[key]) {
      getFromIDB<ExtendMetadata>(key)
        .then((cached) => {
          // Only use cached data if we still don't have memory cache (network request hasn't finished yet)
          if (cached && !metadataCache[key]) {
            metadataCache[key] = cached;
            setMetadata(cached);
            notifyListeners(key, cached);
          }
        })
        .catch((e) => console.error("Failed to load metadata from IDB", e));
    }

    // 2. Start Network Fetch immediately ONLY if not fetched in this session
    if (!fetchedKeys.has(key)) {
      fetchedKeys.add(key);
      setIsLoading(true);
      try {
        const response = await fetch(`/api/jerry/extend`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: API_TOKEN,
          },
          body: JSON.stringify({
            call: "get",
            service_name: "metadata",
            params: {
              report_id: reportId,
              report_type: reportType,
              report_name: reportName,
            },
          }),
        });
        const data = await response.json();
        const index = data.index || {};
        const categories = index.Category
          ? index.Category.map((c: any) => c.name)
          : [];
        let maintainer = index.Maintainer;

        if (typeof maintainer === "string") {
          maintainer = [maintainer];
        } else if (!Array.isArray(maintainer) || maintainer.length === 0) {
          maintainer = [effectiveCreatorName];
        }

        const newMeta = {
          categories: categories,
          maintainer: maintainer,
        };

        // Update memory cache (Authoritative update)
        metadataCache[key] = newMeta;
        setMetadata(newMeta);
        notifyListeners(key, newMeta);

        // Update IDB with fresh data
        await putToIDB(key, newMeta);
      } catch (e) {
        console.error("Failed to fetch extend metadata", e);
        // If failed, allow retrying next time
        fetchedKeys.delete(key);
      } finally {
        setIsLoading(false);
      }
    }
  }, [key, reportId, reportType, reportName, effectiveCreatorName]);

  useEffect(() => {
    // Only fetch if we have valid info
    if (reportId) {
      fetchMetadata();
    }
  }, [fetchMetadata, reportId]);

  const updateMetadata = async (newMetadata: ExtendMetadata) => {
    // Optimistic update
    const prevMetadata = metadataCache[key];
    metadataCache[key] = newMetadata;
    setMetadata(newMetadata);
    notifyListeners(key, newMetadata);

    try {
      // Also update IDB optimistically
      await putToIDB(key, newMetadata);

      await fetch(`/api/jerry/extend`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          token: API_TOKEN,
        },
        body: JSON.stringify({
          call: "post",
          service_name: "update_metadata",
          body: {
            report_id: reportId,
            report_type: reportType,
            categories: newMetadata.categories,
            maintainer: newMetadata.maintainer,
          },
        }),
      });
    } catch (e) {
      console.error("Failed to save", e);
      // Revert on failure
      if (prevMetadata) {
        metadataCache[key] = prevMetadata;
        setMetadata(prevMetadata);
        notifyListeners(key, prevMetadata);
        // Revert IDB as well
        await putToIDB(key, prevMetadata);
      }
    }
  };

  return { metadata, isLoading, updateMetadata };
};
